// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Badge from "@pacific/MobiusOverrides/Badge/Badge";
import { XBadgeModel } from "@pacific/XDL/types";
import { mergeWithCss, sharedBadgesContainerStyles } from "@pacific/XDL/Utilities";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";

interface Props {
    badgeList: XBadgeModel[];
    maxBadges: number;
    extendedStyles?: ProductTextBadgesStyles;
}

export interface ProductTextBadgesStyles {
    container?: InjectableCss;
    badge?: InjectableCss;
    badgeText?: TypographyPresentationProps;
}

export const productTextBadgesStyles: ProductTextBadgesStyles = {
    badgeText: {
        css: css`
            ${wrapInContainerStyles}
        `,
    },
    container: {
        css: css`
            ${sharedBadgesContainerStyles}
            width: fit-content;
            gap: 12px;
        `,
    },
};

const ProductTextBadges = ({ badgeList, maxBadges, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeWithCss(productTextBadgesStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.container} data-test-selector="textBadges">
            {badgeList.slice(0, maxBadges).map(badge => (
                <StyledWrapper key={badge.id} {...styles.badge}>
                    <Badge
                        badgeTag={badge.id}
                        type={badge.badgeType}
                        value={badge.displayText}
                        badgeTextColor={badge.textColorHexCode}
                        badgeBackgroundColor={badge.badgeColorHexCode}
                        badgeTextRounding={badge.badgeStyle === "Round" ? 20 : 0}
                        textBadgeCss={styles.badgeText}
                        {...styles.badge}
                        {...{ badgeBorderColor: badge.borderColor }}
                    />
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default ProductTextBadges;
